import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import QRCode from 'qrcode';
import {
  signIn,
  completeNewPasswordChallenge,
  verifyMfaSetup,
  submitTotpChallenge,
} from '../cognito';
import AuthLayout, { CodeInput, PasswordInput } from '../components/shell/AuthLayout';
import { CopyButton, Icon } from '../components/ui';

/**
 * Cognito-backed login.
 *
 * One card whose body changes with `step`. The shared state (cognitoUser,
 * error, loading) is hoisted so each step transitions without remounting.
 *
 *   credentials  → username + password (initial SRP)
 *   newPassword  → first login: set a real password
 *   mfaSetup     → first login: QR code + 6-digit verify
 *   totp         → returning user: just the 6-digit code
 *
 * On `success` App.jsx (via onLogin) flips the console into the signed-in state.
 */

// The first-login path is three screens long; saying so up front is what stops
// people abandoning it at the QR code, which looks like a dead end otherwise.
const FIRST_LOGIN_STEPS = ['Sign in', 'New password', 'Authenticator'];

const COPY = {
  credentials: { title: 'Sign in', subtitle: 'Use your admin account to continue.' },
  newPassword: { title: 'Set a new password', subtitle: 'First sign-in: replace the temporary password you were sent.' },
  mfaSetup: { title: 'Set up your authenticator', subtitle: 'Scan the code with Google Authenticator, 1Password, Authy or similar, then enter the 6-digit code it shows.' },
  totp: { title: 'Two-step verification', subtitle: 'Enter the 6-digit code from your authenticator app.' },
};

function Login({ onLogin }) {
  const [step, setStep] = useState('credentials');
  const [firstLogin, setFirstLogin] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [cognitoUser, setCognitoUser] = useState(null);
  const [userAttributes, setUserAttributes] = useState({});
  const [mfaSetup, setMfaSetup] = useState({ secretCode: '', qrDataUrl: '' });

  // Generate the QR PNG data URL whenever a fresh secret arrives. Done in an
  // effect so the QR call does not block the state transition.
  useEffect(() => {
    if (!mfaSetup.qrUrl) return undefined;
    let cancelled = false;
    QRCode.toDataURL(mfaSetup.qrUrl, { width: 220, margin: 1 }).then((dataUrl) => {
      if (!cancelled) setMfaSetup((m) => ({ ...m, qrDataUrl: dataUrl }));
    });
    return () => { cancelled = true; };
  }, [mfaSetup.qrUrl]);

  function handleResult(result) {
    if (result.status === 'success') {
      const t = result.tokens;
      onLogin(t.idToken, t.role);
      return;
    }
    if (result.status === 'newPasswordRequired') {
      setCognitoUser(result.cognitoUser);
      setUserAttributes(result.userAttributes || {});
      setFirstLogin(true);
      setStep('newPassword');
      setError('');
      return;
    }
    if (result.status === 'mfaSetup') {
      setCognitoUser(result.cognitoUser);
      setMfaSetup({ secretCode: result.secretCode, qrUrl: result.qrUrl, qrDataUrl: '' });
      setFirstLogin(true);
      setTotpCode('');
      setStep('mfaSetup');
      setError('');
      return;
    }
    if (result.status === 'totpRequired') {
      setCognitoUser(result.cognitoUser);
      setTotpCode('');
      setStep('totp');
      setError('');
      return;
    }
    setError(result.message || 'Login failed');
  }

  async function submitCredentials(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      handleResult(await signIn(username, password));
    } finally {
      setLoading(false);
    }
  }

  async function submitNewPassword(e) {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    setError('');
    try {
      handleResult(await completeNewPasswordChallenge(cognitoUser, newPassword, userAttributes));
    } finally {
      setLoading(false);
    }
  }

  async function submitMfaSetup(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      handleResult(await verifyMfaSetup(cognitoUser, totpCode));
    } finally {
      setLoading(false);
    }
  }

  async function submitTotp(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      handleResult(await submitTotpChallenge(cognitoUser, totpCode));
    } finally {
      setLoading(false);
    }
  }

  const stepIndex = { credentials: 0, newPassword: 1, mfaSetup: 2, totp: 2 }[step];
  const mismatch = confirmPassword.length > 0 && confirmPassword !== newPassword;

  return (
    <AuthLayout
      title={COPY[step].title}
      subtitle={COPY[step].subtitle}
      steps={firstLogin ? FIRST_LOGIN_STEPS : null}
      current={stepIndex}
    >
      {error && (
        <div className="notice notice-danger" role="alert">
          <Icon name="alertCircle" />
          <div className="notice-body">{error}</div>
        </div>
      )}

      {step === 'credentials' && (
        <form onSubmit={submitCredentials}>
          <div className="form-group">
            <label className="form-label" htmlFor="login-username">Username</label>
            <input
              id="login-username"
              type="text"
              className="form-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              spellCheck={false}
              required
              autoFocus
            />
          </div>
          <div className="form-group">
            <div className="row between">
              <label className="form-label" htmlFor="login-password">Password</label>
              <Link to="/forgot-password" className="text-sm">Forgot password?</Link>
            </div>
            <PasswordInput id="login-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <button type="submit" className="btn btn-primary btn-lg btn-block mt-2" disabled={loading}>
            {loading ? <><span className="spinner-sm" /> Signing in…</> : 'Sign in'}
          </button>
        </form>
      )}

      {step === 'newPassword' && (
        <form onSubmit={submitNewPassword}>
          <div className="form-group">
            <label className="form-label" htmlFor="login-new">New password</label>
            <PasswordInput id="login-new" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                           autoComplete="new-password" autoFocus minLength={12} />
            <div className="form-hint">At least 12 characters, with upper and lower case, a digit and a symbol.</div>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="login-confirm">Confirm new password</label>
            <PasswordInput id="login-confirm" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                           autoComplete="new-password" />
            {mismatch && <div className="form-error">Does not match the new password</div>}
          </div>
          <button type="submit" className="btn btn-primary btn-lg btn-block mt-2" disabled={loading}>
            {loading ? <><span className="spinner-sm" /> Saving…</> : 'Set password'}
          </button>
        </form>
      )}

      {step === 'mfaSetup' && (
        <form onSubmit={submitMfaSetup}>
          <div className="auth-qr">
            {mfaSetup.qrDataUrl
              ? <img src={mfaSetup.qrDataUrl} alt="QR code for your authenticator app" width={180} height={180} />
              : <span className="skeleton" style={{ width: 180, height: 180 }} />}
          </div>
          <details className="auth-secret">
            <summary>Can't scan? Enter the key by hand</summary>
            <div className="copy-field mt-2">
              <span>{mfaSetup.secretCode}</span>
              <CopyButton value={mfaSetup.secretCode} label="Copy setup key" />
            </div>
          </details>
          <div className="form-group">
            <label className="form-label" htmlFor="login-mfa">6-digit code</label>
            <CodeInput id="login-mfa" value={totpCode} onChange={setTotpCode} />
          </div>
          <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={loading || totpCode.length !== 6}>
            {loading ? <><span className="spinner-sm" /> Verifying…</> : 'Verify and enable MFA'}
          </button>
        </form>
      )}

      {step === 'totp' && (
        <form onSubmit={submitTotp}>
          <div className="form-group">
            <label className="form-label" htmlFor="login-totp">Code</label>
            <CodeInput id="login-totp" value={totpCode} onChange={setTotpCode} />
          </div>
          <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={loading || totpCode.length !== 6}>
            {loading ? <><span className="spinner-sm" /> Verifying…</> : 'Continue'}
          </button>
          <button type="button" className="btn btn-ghost btn-block mt-2"
                  onClick={() => { setStep('credentials'); setTotpCode(''); setError(''); }}>
            Use a different account
          </button>
        </form>
      )}
    </AuthLayout>
  );
}

export default Login;
